import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, KeyRound, MapPin, Phone } from "lucide-react";
import { ARRONDISSEMENTS, PHONE_DISPLAY, PHONE_FIXE_DISPLAY, PHONE_FIXE_TEL, PHONE_TEL, SERVICES, WHATSAPP_URL } from "../data/site";
import WhatsAppIcon from "./WhatsAppIcon";

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="bg-midnight text-white">
            <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:py-20 lg:px-8">
                <div>
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white shadow-[0_10px_28px_-10px_rgba(0,0,0,0.5)]">
                            <img src={`${process.env.PUBLIC_URL}/serrurier-logo.webp`} alt="Illustration serrurier Paris" className="h-10 w-10 object-contain" width="40" height="40" />
                        </span>
                        <span className="leading-none">
                            <span className="block font-display text-lg font-extrabold tracking-tight">Serrurier Paris</span>
                            <span className="mt-1 block whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.02em] text-white/70">
                                Serrurier Sur Paris & Île-de-France
                            </span>
                        </span>
                    </div>
                    <p className="mt-6 text-sm leading-relaxed text-white/60">
                        Serrurier Paris depuis 1985. Dépannage de serrurerie 24h/24 et 7j/7 sur Paris et toute
                        l'Île-de-France. Devis gratuit, agréé assurance.
                    </p>
                </div>

                <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Nos services</h3>
                    <ul className="mt-5 space-y-3 text-sm">
                        {SERVICES.map((s) => (
                            <li key={s.title}>
                                <Link to={s.slug} className="text-white/70 transition-colors hover:text-gold">
                                    {s.title}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link to="/services" data-testid="footer-services-link" className="font-semibold text-gold hover:underline">
                                Tous les services →
                            </Link>
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Arrondissements</h3>
                    <div className="mt-5 flex flex-wrap gap-x-3 gap-y-2 text-sm text-white/70">
                        {ARRONDISSEMENTS.map((a) => (
                            <Link key={a.n} to={`/serrurier-paris-${a.n}`} className="transition-colors hover:text-gold">
                                {a.label}
                            </Link>
                        ))}
                    </div>
                    <p className="mt-4 text-sm text-white/50">+ toute l'Île-de-France : 77, 78, 91, 92, 93, 94, 95</p>
                    <Link to="/zones-d-intervention" data-testid="footer-zones-link" className="mt-3 inline-block text-sm font-semibold text-gold hover:underline">
                        Toutes les zones d'intervention →
                    </Link>
                </div>

                <div>
                    <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Contact urgence</h3>
                    <a
                        href={PHONE_TEL}
                        data-testid="footer-call-button"
                        className="mt-5 flex items-center gap-3 text-xl font-bold text-white transition-colors hover:text-gold"
                    >
                        <Phone className="h-5 w-5 text-emergency" /> {PHONE_DISPLAY}
                        <span className="text-xs font-semibold uppercase tracking-wider text-white/40">Urgence</span>
                    </a>
                    <a
                        href={PHONE_FIXE_TEL}
                        data-testid="footer-fixe-button"
                        className="mt-3 flex items-center gap-3 text-xl font-bold text-white transition-colors hover:text-gold"
                    >
                        <Phone className="h-5 w-5 text-gold" /> {PHONE_FIXE_DISPLAY}
                        <span className="text-xs font-semibold uppercase tracking-wider text-white/40">Devis</span>
                    </a>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-link"
                        className="mt-3 flex items-center gap-3 text-sm font-semibold text-white/70 transition-colors hover:text-gold"
                    >
                        <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp direct
                    </a>
                    <div className="mt-5 space-y-2 text-sm text-white/60">
                        <p className="flex items-center gap-2">
                            <Clock className="h-4 w-4" /> 24h/24 — 7j/7, même jours fériés
                        </p>
                        <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" /> Paris & Île-de-France
                        </p>
                    </div>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-4 py-6 pb-32 text-xs text-white/40 sm:px-6 md:flex-row md:items-center md:pb-6 lg:px-8">
                    <p>© 2026 Serrurier Paris — Artisan serrurier depuis 1985. Tous droits réservés.</p>
                    <div className="flex flex-wrap gap-6">
                        <Link to="/faq" data-testid="footer-faq-link" className="transition-colors hover:text-gold">
                            FAQ
                        </Link>
                        <Link to="/blog" data-testid="footer-blog-link" className="transition-colors hover:text-gold">
                            Blog
                        </Link>
                        <Link to="/tarifs" className="transition-colors hover:text-gold">
                            Tarifs
                        </Link>
                        <Link to="/devis-contact" data-testid="footer-contact-link" className="transition-colors hover:text-gold">
                            Contact
                        </Link>
                        <Link to="/mentions-legales" data-testid="footer-legal-link" className="transition-colors hover:text-gold">
                            Mentions légales
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
